// The badge lab's reveal bench: fake an unrevealed copy of any badge, at any tier,
// first on the site or not, any copy number, and watch it play through the real
// BadgeRevealView, the view the host renders, with its grouping and worst-to-best
// order. "Earn it" takes the level the tier gives (the rule the server uses); the
// level buttons force one. The share story underneath shows the same fake. "History
// of N" fakes a member's history (backfill copies) the way launch awarded it. Local
// state only; no request leaves the page. The log shows what the real host would
// have sent.
import React, { useCallback, useMemo, useRef, useState } from "react";
import { Button } from "../../../ui";
import { useBadgeLib, type BadgeLib } from "../art";
import { RARITY_CLASSES, TIERS, type RarityClass, type RevealLevel, type Tier, type UnrevealedCopy } from "../types";
import BadgeRevealView from "./BadgeRevealView";
import BadgeStoryFrame from "../share/BadgeStoryFrame";
import { tierLabel } from "../collection/badgeFormat";
import { copyValue, roundValue } from "../value";
import { collectionPath, levelFor } from "./revealQueue";

const DEFAULT_HOLDERS: Record<RarityClass, number> = { common: 30, uncommon: 14, rare: 5, epic: 2, legendary: 1 };
const MEMBERS = 41;
const DAY_MS = 86_400_000;

/**
 * A history as launch awarded it: some badges earned many times (one member holds
 * a two-day streak x71) and single badges across the catalogue, by rarity.
 */
export const HISTORY_SIZES = [2, 12, 180] as const;
type HistorySize = (typeof HISTORY_SIZES)[number];
const HISTORY_MIX: Record<HistorySize, { repeats: Array<[string, number]>; picks: Record<RarityClass, number> }> = {
  2: { repeats: [], picks: { common: 1, uncommon: 0, rare: 0, epic: 1, legendary: 0 } },
  12: { repeats: [["AOTD_STREAK_2", 3]], picks: { common: 3, uncommon: 2, rare: 2, epic: 1, legendary: 1 } },
  180: {
    repeats: [
      ["AOTD_STREAK_2", 71],
      ["RACE_TIME_750", 24],
      ["RACE_TIME_500", 14],
      ["RACE_STREAK_3", 12],
      ["AOTD_STREAK_7", 9],
      ["RACE_STREAK_7", 4],
    ],
    picks: { common: 18, uncommon: 12, rare: 8, epic: 6, legendary: 2 },
  },
};

/**
 * The engine's rarity class for a fake, close enough: gems are epic, gold rare,
 * silver uncommon. The SPA shows the tier; the class only fills the API shape.
 */
function rarityForTier(tier: string, unique: boolean): RarityClass {
  if (unique || tier === "DIAMOND") return "legendary";
  if (tier === "RUBY" || tier === "EMERALD") return "epic";
  if (tier === "GOLD") return "rare";
  if (tier === "SILVER") return "uncommon";
  return "common";
}

type LibBadge = BadgeLib["badges"][number];

interface FakeOptions {
  level: RevealLevel;
  rarity: RarityClass;
  /** Draw the badge at this tier instead of its own. */
  tier?: Tier;
  holders: number;
  members: number;
  firstEver: boolean;
  repeatIndex: number;
}

let fakeSeq = 0;

function fakeCopy(lib: BadgeLib, b: LibBadge, o: FakeOptions): UnrevealedCopy {
  fakeSeq += 1;
  const cat = lib.categories[b.category];
  const points = typeof b.points === "number" ? b.points : 5;
  const pct = o.members ? Math.round((o.holders / o.members) * 1000) / 10 : 0;
  return {
    code: b.code,
    name: b.name,
    description: b.description ?? "",
    category: b.category,
    family: cat?.family ?? "",
    tier: o.tier ?? (b.tier as Tier),
    number: b.number,
    threshold: b.number ?? 1,
    points,
    repeatable: !!cat?.repeatable,
    unique: b.unique === true,
    rarity: { class: o.rarity, holders: o.holders, members: o.members, pct },
    copyId: `lab-${fakeSeq}`,
    // a second apart, so the queue keeps the order they were made in
    dateEarned: new Date(Date.now() - 3_600_000 + fakeSeq * 1000).toISOString(),
    repeatIndex: o.repeatIndex,
    count: o.repeatIndex,
    backfill: o.level === "backfill",
    firstEver: o.firstEver,
    revealLevel: o.level,
    rarityAtEarn: { class: o.rarity, holders: o.holders, members: o.members },
    // what the backend sends: each repeat keeps 95.5% of the copy before it
    value: roundValue(copyValue(points, o.repeatIndex)),
  };
}

/** N history copies, oldest a year back. */
function fakeHistory(lib: BadgeLib, n: HistorySize): UnrevealedCopy[] {
  const mix = HISTORY_MIX[n];
  const out: UnrevealedCopy[] = [];
  const history = (b: LibBadge, repeatIndex: number, count: number, daysAgo: number) => {
    const rarity = rarityForTier(b.tier, b.unique === true);
    const copy = fakeCopy(lib, b, { level: "backfill", rarity, holders: DEFAULT_HOLDERS[rarity], members: MEMBERS, firstEver: false, repeatIndex });
    out.push({ ...copy, count, dateEarned: new Date(Date.now() - daysAgo * DAY_MS).toISOString() });
  };
  const used = new Set<string>();
  mix.repeats.forEach(([code, copies]) => {
    const b = lib.badges.find((x) => x.code === code);
    if (!b) return;
    used.add(code);
    for (let k = 1; k <= copies; k++) history(b, k, copies, 360 - k * 4);
  });
  // single badges spread over the catalogue, the right number of each rarity
  RARITY_CLASSES.forEach((rarity) => {
    const want = mix.picks[rarity];
    const pool = lib.badges.filter(
      (b) => !used.has(b.code) && !lib.categories[b.category]?.repeatable && rarityForTier(b.tier, b.unique === true) === rarity
    );
    for (let k = 0; k < want && pool.length; k++) {
      const b = pool[Math.floor((k * pool.length) / want) % pool.length];
      if (used.has(b.code)) continue;
      used.add(b.code);
      history(b, 1, 1, 300 - out.length);
    }
  });
  return out;
}

/** A copy id list for the log: short lists in full, long ones counted. */
function idsText(ids: string[]): string {
  return ids.length <= 3 ? JSON.stringify(ids) : `[${ids.length} copies]`;
}

const RevealLab: React.FC = () => {
  const { lib } = useBadgeLib();
  const badges = useMemo(() => lib?.badges ?? [], [lib]);
  const [code, setCode] = useState("AOTD_NUMBER_1");
  /** "" draws the badge at its own tier. */
  const [tierPick, setTierPick] = useState<Tier | "">("");
  const [holders, setHolders] = useState(DEFAULT_HOLDERS.legendary);
  const [members, setMembers] = useState(MEMBERS);
  const [firstEver, setFirstEver] = useState(false);
  const [repeatIndex, setRepeatIndex] = useState(1);
  const [shareFails, setShareFails] = useState(false);
  const [historySize, setHistorySize] = useState<HistorySize>(180);
  const [items, setItems] = useState<UnrevealedCopy[]>([]);
  const [log, setLog] = useState<string[]>([]);
  const shareSeq = useRef(0);

  const byCategory = useMemo(() => {
    const groups = new Map<string, LibBadge[]>();
    badges.forEach((b) => {
      const list = groups.get(b.category) ?? [];
      list.push(b);
      groups.set(b.category, list);
    });
    return Array.from(groups.entries());
  }, [badges]);

  const picked = badges.find((b) => b.code === code) ?? badges[0];
  const tier: Tier = tierPick || ((picked?.tier as Tier | undefined) ?? "PAPER");
  const earnLevel = levelFor({ tier, firstEver, repeatIndex });

  const note = useCallback((line: string) => setLog((prev) => [line, ...prev].slice(0, 8)), []);
  const drop = useCallback((ids: string[]) => setItems((prev) => prev.filter((i) => !ids.includes(i.copyId))), []);

  const add = (level: RevealLevel, howMany = 1) => {
    if (!lib || !picked) return;
    const start = Math.max(0, badges.indexOf(picked));
    const made: UnrevealedCopy[] = [];
    for (let k = 0; k < howMany; k++) {
      // more than one: walk the catalogue from the picked badge so the tiles differ
      const b = howMany === 1 ? picked : badges[(start + k * 3) % badges.length];
      const own = howMany === 1 ? tier : tierPick || (b.tier as Tier);
      made.push(fakeCopy(lib, b, { level, rarity: rarityForTier(own, b.unique === true), tier: own, holders, members, firstEver, repeatIndex }));
    }
    setItems((prev) => [...prev, ...made]);
  };

  const addEverything = () => {
    if (!lib || !picked) return;
    const at = (k: number) => badges[(Math.max(0, badges.indexOf(picked)) + k * 5) % badges.length];
    const base = { holders, members, firstEver: false, repeatIndex: 1 };
    // each at the level its tier gives, the way the server sends them
    const fake = (b: LibBadge, t: Tier, over: Partial<FakeOptions> = {}) => {
      const o = { ...base, ...over };
      return fakeCopy(lib, b, { ...o, tier: t, rarity: rarityForTier(t, false), level: levelFor({ tier: t, firstEver: o.firstEver, repeatIndex: o.repeatIndex }) });
    };
    setItems((prev) => [
      ...prev,
      fake(at(3), "PAPER", { holders: 30 }),
      fake(at(0), "RUBY", { holders: 2 }),
      fake(at(1), "GOLD", { holders: 6 }),
      fake(at(2), "BRONZE", { holders: 1, firstEver: true }),
      fake(at(4), "BRONZE", { holders: 12, repeatIndex: 3 }),
      ...Array.from({ length: 9 }, (_, k) => fakeCopy(lib, at(6 + k), { ...base, level: "backfill", rarity: "common", holders: 20 })),
    ]);
  };

  const addHistory = () => {
    if (!lib) return;
    setItems((prev) => [...prev, ...fakeHistory(lib, historySize)]);
  };

  const onTier = (t: Tier | "") => {
    setTierPick(t);
    setHolders(DEFAULT_HOLDERS[rarityForTier(t || picked?.tier || "PAPER", picked?.unique === true)]);
  };

  const num = (v: string, min: number) => Math.max(min, Math.floor(Number(v) || 0));
  const counts = items.reduce<Record<string, number>>((acc, i) => {
    acc[i.revealLevel] = (acc[i.revealLevel] ?? 0) + 1;
    return acc;
  }, {});

  return (
    <div>
      <div className="flex flex-wrap items-end gap-3 text-sm mb-3">
        <label className="flex flex-col gap-1">
          Badge
          <select className="border-2 border-black px-1 max-w-[260px]" value={picked?.code ?? ""} onChange={(e) => setCode(e.target.value)}>
            {byCategory.map(([cat, list]) => (
              <optgroup key={cat} label={cat}>
                {list.map((b) => (
                  <option key={b.code} value={b.code}>
                    {b.name} ({b.tier.toLowerCase()})
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1">
          Tier
          <select className="border-2 border-black px-1" value={tierPick} onChange={(e) => onTier(e.target.value as Tier | "")}>
            <option value="">its own ({tierLabel(picked?.tier ?? "PAPER")})</option>
            {TIERS.map((t) => (
              <option key={t} value={t}>
                {tierLabel(t)}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1">
          Holders
          <input className="border-2 border-black px-1 w-16" type="number" min={1} value={holders} onChange={(e) => setHolders(num(e.target.value, 1))} />
        </label>
        <label className="flex flex-col gap-1">
          Members
          <input className="border-2 border-black px-1 w-16" type="number" min={1} value={members} onChange={(e) => setMembers(num(e.target.value, 1))} />
        </label>
        <label className="flex flex-col gap-1">
          Copy
          <select className="border-2 border-black px-1" value={repeatIndex} onChange={(e) => setRepeatIndex(Number(e.target.value))}>
            {[1, 2, 3, 5, 10, 25].map((n) => (
              <option key={n} value={n}>
                {n === 1 ? "first" : `x${n}`}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-1 pb-1">
          <input type="checkbox" checked={firstEver} onChange={(e) => setFirstEver(e.target.checked)} />
          First on the site
        </label>
        <label className="flex items-center gap-1 pb-1">
          <input type="checkbox" checked={shareFails} onChange={(e) => setShareFails(e.target.checked)} />
          Share fails
        </label>
      </div>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <Button type="button" size="sm" variant="sky" onClick={() => add(earnLevel)} disabled={!lib}>
          Earn it ({earnLevel})
        </Button>
        <Button type="button" size="sm" onClick={() => add("cinematic")} disabled={!lib}>
          Cinematic
        </Button>
        <Button type="button" size="sm" onClick={() => add("card")} disabled={!lib}>
          Card
        </Button>
        <Button type="button" size="sm" onClick={() => add("tile")} disabled={!lib}>
          Tile
        </Button>
        <Button type="button" size="sm" onClick={() => add("tile", 5)} disabled={!lib}>
          Five tiles
        </Button>
        <span className="inline-flex items-center gap-1">
          <select
            aria-label="History size"
            className="border-2 border-black px-1 h-8"
            value={historySize}
            onChange={(e) => setHistorySize(Number(e.target.value) as HistorySize)}
          >
            {HISTORY_SIZES.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
          <Button type="button" size="sm" onClick={addHistory} disabled={!lib}>
            History of {historySize}
          </Button>
        </span>
        <Button type="button" size="sm" onClick={addEverything} disabled={!lib}>
          A busy morning
        </Button>
        <Button type="button" size="sm" variant="destructive" onClick={() => setItems([])} disabled={!items.length}>
          Clear
        </Button>
      </div>
      <p className="text-xs text-gray-600 mb-2">
        Queued: {items.length ? Object.entries(counts).map(([k, v]) => `${v} ${k}`).join(", ") : "nothing"}. Cinematics and cards wait while a text field has focus, so click
        outside the number fields first. Reduced motion follows the system setting.
      </p>
      {log.length > 0 && (
        <ol className="text-xs font-mono bg-[#f4f1e8] border-2 border-black p-2 space-y-0.5">
          {log.map((line, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <li key={i}>{line}</li>
          ))}
        </ol>
      )}
      {picked && (
        <details className="mb-3 text-sm">
          <summary className="cursor-pointer font-bold">Share story with these settings</summary>
          <div className="mt-2" style={{ width: 240 }}>
            <BadgeStoryFrame
              input={{
                badge: { code: picked.code, name: picked.name, category: picked.category, tier, number: picked.number, unique: picked.unique === true },
                username: "s4b",
                count: repeatIndex,
                date: new Date().toISOString(),
                holders,
                members,
                firstEver,
              }}
            />
          </div>
        </details>
      )}
      <BadgeRevealView
        items={items}
        openDelayMs={300}
        onCollect={(ids) => {
          drop(ids);
          note(`POST /me/collect {"copyIds":${idsText(ids)}}`);
        }}
        onOpenBadge={(badgeCode, ids) => {
          drop(ids);
          note(`POST /me/collect {"copyIds":${idsText(ids)}}, then open ${collectionPath(badgeCode)}`);
        }}
        onShare={async (group) => {
          await new Promise((r) => setTimeout(r, 600));
          if (shareFails) {
            note(`POST /me/share {"code":"${group.code}"} failed`);
            throw new Error("lab: share fails");
          }
          shareSeq.current += 1;
          drop(group.copyIds);
          note(`POST /me/share {"code":"${group.code}"}, collect ${idsText(group.copyIds)}, then open /badge/s/lab${shareSeq.current}`);
        }}
      />
    </div>
  );
};

export default RevealLab;
